import axios from "axios";
import Storage from "../utils/Storage";
import Config from "../config";
import { CheckboxValueType } from "antd/es/checkbox/Group";
import moment, { Moment } from "moment";
import { handleScore } from "../utils/common";
import _ from "lodash-es";

export enum PublishStatus {
    No = "0", // 未发布
    Waiting = "1", // 取消发布待发布
    Yes = "2" //已发布
}

export enum GroupType {
    base = "0", //固定小组
    random = "1" //随机小组
}

export enum Step {
    Step1, //第一步
    Step2, //第二步
    Step3 //第三步
}

export const PublishMap = {
    [PublishStatus.No]: "待发布",
    [PublishStatus.Waiting]: "待发布",
    [PublishStatus.Yes]: "已发布"
};

export interface GroupListItem {
    id: number; //小组id
    groupName: string; //小组名称
    groupType: string; //小组类型（0固定小组、1随机小组）
    memberCount: number; //小组成员数
    customerCode: string; //用户代码
    batchNo: string; //学期编号
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
    weektimeId: number; //周次节id
    delStatus: number; //删除标识（0否1是）
    createTime: string; //创建时间
    updateTime: string; //修改时间
    createUser: number; //创建人id
    updateUser: number; //修改人id
}

interface QueryGroupParams {
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
    groupName?: string; //小组名称
    groupType?: GroupType; //小组类型（0固定小组、1随机小组）
    weektimeId?: number; //周次节id(当小组类型为随机小组时为必填项)
}

/**
 * 查询当前课下的小组
 * @param data
 */
export function getGroupList(data: QueryGroupParams) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const customerCode = Config.CustomCode;
    return axios
        .post<SuccessData<GroupListItem[]>>("/teacher/courseGroup/queryGroup", {
            ...data,
            batchNo,
            customerCode
        })
        .then(res => res.data.data);
}

export function updateGroup(id: number, groupName: string) {
    return axios.post<SuccessData<GroupListItem[]>>(
        "/teacher/courseGroup/updateGroup",
        {
            id,
            groupName
        }
    );
}

//随即小组改名
export function adjustRandomGroupName(
    id: number,
    groupName: string,
    randomGroupKey: string
) {
    return axios.post<SuccessData<GroupListItem[]>>(
        "/teacher/courseGroup/adjustRandomGroupName",
        {
            id,
            groupName,
            randomGroupKey
        }
    );
}

interface AutoGroupParams {
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
    weektimeId: number; //周次节id（自动生成随机小组时，为必填项）
    groupType: GroupType; //小组类型（0固定小组、1随机小组）为空时为固定小组
    groupCount: string; //小组数量
    autoSetGroupLeader: string; //是否自动设置小组长（0否1是）
}

/**
 * 自动生成小组
 * @param params
 */
export function autoGroup(params: AutoGroupParams) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const customerCode = Config.CustomCode;
    return axios.post<SuccessData<GroupListItem[]>>(
        "/teacher/courseGroup/autoGroup",
        {
            ...params,
            batchNo,
            customerCode
        }
    );
}

export interface GroupMemberListItem {
    id: number; //小组成员id
    groupId: number; //小组id
    groupName: string; //小组名称
    memberNo: string; //成员学号
    name: string; //成员姓名
    college: string; //成员所属院系
    major: string; //成员专业
    className: string; //成员所属班级名称
    isGroupLeader: BooleanValue; //是否是组长（0否1是）
    delStatus: number; //删除标识符（0否1是）
    createTime: string; //创建时间
    updateTime: string; //修改时间
    createUser: number; //创建人id
    updateUser: number; //修改人id
    groupScore: string; //小组得分
    memberScore: string; //成员得分
    status: string; //活动状态
    partInTimeStr: string; //参与时间
}

/**
 * 查询小组成员
 */
export function queryGroupMember(groupId: number, name?: string) {
    const newName = name ? name.trim() : undefined;
    return axios
        .post<SuccessData<GroupMemberListItem[]>>(
            "/teacher/courseGroup/queryGroupMember",
            {
                groupId,
                name: newName
            }
        )
        .then(res => res.data.data);
}

/**
 * 设置小组成员为小组长
 * @param id:小组成员id
 * @param groupId:小组id
 */
export function setGroupLeader(id: number, groupId: number) {
    return axios.post<SuccessData<GroupMemberListItem[]>>(
        "/teacher/courseGroup/setGroupLeader",
        {
            id,
            groupId
        }
    );
}

/**
 * 设置小组成员为小组长-随即小组
 * @param memberNo:小组成员学号
 * @param groupId:小组id
 * @param randomGroupKey
 */
export function adjustRandomGroupMemberLeader(
    memberNo: string,
    groupId: number,
    randomGroupKey: string
) {
    return axios.get(
        `/teacher/courseGroup/adjustRandomGroupMemberLeader/${randomGroupKey}/${groupId}/${memberNo}`
    );
}

/**
 * 删除小组
 * @param ids
 */
export function deleteGroup(ids: number) {
    return axios.post<SuccessData<GroupMemberListItem[]>>(
        "/teacher/courseGroup/deleteGroup",
        { ids }
    );
}

//随即小组,删除小组
export function deleteRandomGroup(id: number, randomGroupKey: string) {
    return axios.get(
        `/teacher/courseGroup/adjustRandomGroupDel/${randomGroupKey}/${id}`
    );
}

export interface NoGroupStudentListItem {
    memberNo: string; //学生学号
    name: string; //学生姓名
    college: string; //学生所属院系
    major: string; //学生专业
    className: string; //学生所属班级名称
}

export interface NoGroupStudentParams {
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
    groupType: GroupType; //小组类型（0固定小组、1随机小组）
    weektimeId?: number; //周次节id(当给随机小组添加成员时为必填项)
}

/**
 * 查询还未分组的学生
 */
export function queryNoGroupStudent(params: NoGroupStudentParams) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const customerCode = Config.CustomCode;
    return axios
        .post<SuccessData<NoGroupStudentListItem[]>>(
            "/teacher/courseGroup/queryNoGroupStudent",
            { ...params, batchNo, customerCode }
        )
        .then(res => res.data.data);
}

/**
 * 查询还未分组的学生-随机分组
 */
export interface NoRandomGroupStudentParams {
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
    randomGroupKey: string;
}
export function adjustRandomGroupMemberQueryNotGroupStudent(
    params: NoRandomGroupStudentParams
) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const customerCode = Config.CustomCode;
    return axios
        .post<SuccessData<NoGroupStudentListItem[]>>(
            "/teacher/courseGroup/adjustRandomGroupMemberQueryNotGroupStudent",
            { ...params, batchNo, customerCode }
        )
        .then(res => res.data.data);
}

interface addGroupMemberParams {
    groupId?: number; //小组id
    memberNo: string; //学生学号
    name: string; //学生姓名
    college: string; //学生所属院系
    major: string; //学生专业
    className: string; //学生所属班级姓名
    isGroupLeader: string; //是否是小组长（0否1是）
}

/**
 * 新增小组成员
 * @param params
 */
export function addGroupMember(params: addGroupMemberParams[]) {
    return axios
        .post("/teacher/courseGroup/addGroupMember", params)
        .then(res => res.data.data);
}

/**
 * 新增小组成员
 * @param params
 */
export interface addRandomGroupMemberParams extends addGroupMemberParams {
    randomGroupKey: string;
}

export function adjustRandomGroupMemberAdd(
    params: addRandomGroupMemberParams[]
) {
    return axios
        .post("/teacher/courseGroup/adjustRandomGroupMemberAdd", params)
        .then(res => res.data.data);
}

/**
 * 删除小组成员
 * @param ids
 */
export function deleteGroupMember(ids: string) {
    return axios.post("/teacher/courseGroup/deleteGroupMember", { ids: ids });
}

/**
 * 删除小组成员-随即小组
 * @param ids
 */
export function adjustRandomGroupMemberDelBatch(ids: string) {
    return axios.post("/teacher/courseGroup/adjustRandomGroupMemberDelBatch", {
        ids: ids
    });
}

export interface activityBaseInfoItem {
    id: number; //小组活动id
    activityName: string; //小组活动名称
    deadline: string; //截止日期
    deadlineStr: string; //截止日期（yyyy-MM-dd HH:mm:ss）
    groupType: BooleanValue; //小组类型（0固定小组 1随机小组）
    groupIds: string; //参与小组id列表（用,号隔开）
    needTaskAllocation: string; //需要提交小组成员任务分配 0否1是
    onlySupportTeacherGrade: string; //仅支持教师评分 0否1是
    onlySupportTeacherRemark: string; //仅支持教师评语 0否1是
    hasTask: BooleanValue; //是否有活动任务（0否1是）
    publishStatus: PublishStatus; //发布状态（0未发布 1取消发布待发布 2已发布）
    customerCode: string; //用户代码
    batchNo: string; //学期编号
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
    weektimeId: number; //id
    delStatus: number; //删除标识
    createTime: string; //创建时间
    updateTimeL: string; //修改时间
    createUser: number; //创建人id
    updateUser: number; //修改人id
    randomGroupKey?: string;
    signInId?: number;
}

interface dcGroupActivityStaticsItem {
    totalGroupCount: number; //总参与小组数量
    hasCorrectedCount: number; //已批阅数量
    notCorrectedCount: number; //未批阅数量
    hasPartInCount: number; //已参与数量
    notPartInCount: number; //未参与数量
}

export interface activityListItem {
    activityBaseInfo: activityBaseInfoItem; //小组活动基本信息
    dcGroupActivityStatics: dcGroupActivityStaticsItem;
}

/**
 * 多处使用，谨慎修改
 */
interface activityListParams {
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
    weektimeId: number; //周次节id
}

/**
 * 查询小组活动列表
 * @param params
 */
export function queryGroupActivityList(params: activityListParams) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const customerCode = Config.CustomCode;
    return axios
        .post<SuccessData<activityListItem[]>>(
            "/teacher/courseGroupActivity/queryGroupActivityList",
            { ...params, batchNo, customerCode }
        )
        .then(res => res.data.data);
}

/**
 * 取消发布小组活动
 * @param id
 */
export function cancelGroupActivity(id: number) {
    return axios.post("/teacher/courseGroupActivity/cancelGroupActivity", {
        id
    });
}

/**
 * 批量取消小组活动
 * @param id
 */
export function cancelGroupActivityBatch(id: CheckboxValueType[]) {
    const params = id.map(item => ({ id: item.toString() }));

    return axios.post(
        "/teacher/courseGroupActivity/cancelGroupActivityBatch",
        params
    );
}

interface StepOneParams {
    id?: number;
    activityName: string; //活动名称
    deadlineStr: string; //截止日期（yyyy-MM-dd hh:mm:ss）
    needTaskAllocation?: string; //需要提交小组成员任务分配 0否1是
    onlySupportTeacherGrade?: string; //仅支持教师评分 0否1是
    onlySupportTeacherRemark?: string; //仅支持教师评语 0否1是
    courseNo: string; //课程编号
    groupType: GroupType; //小组类型（0固定小组1随机小组）
    groupIds: string; //参与小组id列表（用,号隔开）
    courseSeq: string; //课程序号
    weektimeId: number; //周次节id
    randomGroupKey?: string; //周次节id
    signInId?: number;
}

/**
 * 新建小组活动-第一步：保存小组活动基本信息
 * @param params
 */
export function saveGroupActivityBaseInfo(params: StepOneParams) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const customerCode = Config.CustomCode;
    return axios
        .post<SuccessData<activityBaseInfoItem>>(
            "/teacher/courseGroupActivity/saveGroupActivityBaseInfo",
            {
                ...params,
                batchNo,
                customerCode
            }
        )
        .then(res => res.data.data);
}

/**
 * 新建小组活动-第二步：查询当前小组活动下，还没有活动任务的小组
 * @param activityId 小组活动id
 * @param id 任务id（有就传，没有就不传）
 */
export function getNotTaskActivityGroup(activityId: number, id?: number) {
    return axios
        .post<SuccessData<GroupListItem[]>>(
            "/teacher/courseGroupActivity/getNotTaskActivityGroup",
            {
                activityId,
                id
            }
        )
        .then(res => res.data.data);
}

export interface ActivityTaskListItem {
    id: number; //任务id
    activityId: number; //活动id
    taskName: string; //任务名称
    taskQuestion: string; //任务问题
    groupIds: string; //参与小组
    groupNames: string; //小组名
    score: number; //分值
}

/**
 * 新建小组活动-第二步:查询活动下的任务列表
 * @param activityId
 */
export function queryActivityTaskList(activityId: number) {
    return axios
        .get<SuccessData<ActivityTaskListItem[]>>(
            "/teacher/courseGroupActivity/queryActivityTaskList",
            {
                params: { activityId }
            }
        )
        .then(res => res.data.data);
}

export interface TaskFileEntityListItem {
    key?: string;
    id?: number; //任务附件id
    taskHomeworkId?: number; //所属任务id
    fileName: string; //附件名称
    fileUrl: string; //附件地址
    viewUrl?: string; //预览地址
    fileSize?: string; //附件大小
    fileSizeNum: number;
    fileType?: string; //附件类型
    uploadType?: BooleanValue; //上传类型（0任务1作业）
}

export interface ActivityTaskDetail extends ActivityTaskListItem {
    taskFileEntityList?: TaskFileEntityListItem[];
}

/**
 * 新建小组活动-第二步:查询任务明细
 * @param taskId
 */
export function getActivityTaskDetail(taskId: number) {
    return axios
        .get<SuccessData<ActivityTaskDetail>>(
            "/teacher/courseGroupActivity/getActivityTaskDetail",
            {
                params: {
                    taskId
                }
            }
        )
        .then(res => res.data.data);
}

interface GroupActivityTaskInfo extends ActivityTaskListItem {
    taskFileEntityList: TaskFileEntityListItem[];
}

interface ActivityTaskParams {
    id?: number; //任务id(为空时新增，不为空时修改)
    activityId: number; //所属小组活动id
    groupIds: string; //参与小组id列表（用,号隔开）
    taskName: string; //任务名称
    taskQuestion: string; //任务问题
    taskFileEntityList?: {
        id?: number;
        fileName: string; //文件名称
        fileUrl: string; //文件url
        fileSizeNum: number; //文件大小
    }[];
    score: number;
}

/**
 * 新建小组活动-第二步：新增/修改活动任务
 * @param params
 */
export function saveGroupActivityTaskInfo(params: ActivityTaskParams) {
    return axios
        .post<SuccessData<GroupActivityTaskInfo>>(
            "/teacher/courseGroupActivity/saveGroupActivityTaskInfo",
            {
                ...params
            }
        )
        .then(res => res.data.data);
}

/**
 * 新建小组活动-第二步：删除任务附件
 * @param ids
 */
export function deleteTaskFile(ids?: number) {
    return axios.post("/teacher/courseGroupActivity/deleteTaskFile", {
        ids
    });
}

/**
 * 新建小组活动-第二步：删除任务附件
 * @param params
 */
export function deleteGroupActivityTaskInfo(ids: number) {
    return axios.post(
        "/teacher/courseGroupActivity/deleteGroupActivityTaskInfo",
        {
            ids
        }
    );
}

interface GroupActivityDetail {
    activityBaseInfo: activityBaseInfoItem;
    taskList: ActivityTaskDetail[];
}

/**
 * 新建小组活动-第三步：预览小组活动
 * @param id
 */
export function queryGroupActivityDetail(id: number) {
    return axios
        .post<SuccessData<GroupActivityDetail>>(
            "/teacher/courseGroupActivity/queryGroupActivityDetail",
            {
                id
            }
        )
        .then(res => res.data.data);
}

/**
 * 发布小组活动
 */

export function publishGroupActivity(id: number, clearStatus?: string) {
    return axios.post<SuccessData<GroupActivityDetail>>(
        "/teacher/courseGroupActivity/publishGroupActivity",
        {
            id,
            clearStatus
        }
    );
}

/**
 * 批量发布小组活动
 */

export function publishGroupActivityBatch(params: { id: CheckboxValueType }[]) {
    return axios.post<SuccessData<GroupActivityDetail>>(
        "/teacher/courseGroupActivity/publishGroupActivityBatch",
        [...params]
    );
}

/**
 * 删除小组活动
 */

export function deleteGroupActivity(id: number) {
    return axios.post("/teacher/courseGroupActivity/deleteGroupActivity", {
        id
    });
}

/**
 * 批量删除小组活动
 */

export function deleteGroupActivityBatch(params: { id: CheckboxValueType }[]) {
    return axios.post("/teacher/courseGroupActivity/deleteGroupActivityBatch", [
        ...params
    ]);
}

export interface groupActivityDetailStaticsList {
    activityId: number; //活动id
    taskId: number; //任务id
    statusId: number; //状态id
    status: string; //小组活动状态：0待参与 1待提交 2已参与
    hasCorrected: string; //是否已批阅（0否1是）
    homeworkId: number; //作业id
    groupId: number; //小组id
    groupName: string; //小组名称
    groupType: null; //小组类型
    groupLeaderId: number; //小组组长id
    groupLeaderNo: string; //小组组长学号
    groupLeaderName: string; //小组组长姓名
    groupCount: number; //小组成员数量
    partInTimeStr: string; //参与时间str(yyyy-MM-dd hh:mm:ss)
    groupScore: number; //小组分数
    memberList: GroupMemberListItem[];
}

interface GroupActivityStatics {
    activity: activityBaseInfoItem;
    totalGroupCount: number;
    hasCorrectedCount: number;
    notCorrectedCount: number;
    notPartInCount: number;
    groupActivityDetailStaticsList: groupActivityDetailStaticsList[];
}

/**
 * 查询小组活动统计详情
 * @param id
 */
export function queryGroupActivityStatics(id: number) {
    return axios
        .post<SuccessData<GroupActivityStatics>>(
            "/teacher/courseGroupActivity/queryGroupActivityStatics",
            { id }
        )
        .then(res => res.data.data);
}

interface ForCheckGroup extends GroupListItem {
    groupMemberList: GroupMemberListItem[];
}

export interface MemberGradeListItem {
    activityId: number; //活动id
    taskId: number; //任务id
    statusId: number; //任务状态id
    groupId: number; //小组id
    groupName: string; //小组名称
    memberId: string; //成员id
    memberNo: string; //成员学号
    memberName: string; //成员姓名
    memberCollege: string; //成员院系
    memberMajor: string; //成员专业
    memberClassName: string; //成员班级名称
    memberIsGroupLeader: string; //是否是小组长（0否1是）
    partInStatus: string; //参与状态（0待参与 1待提交 2已参与）
    groupScore: number; //小组得分
    memberScore: number; //成员得分
    memberGradeId: number; //小组成员得分id
}

export interface ForCheckHomework {
    homeworkFileList: TaskFileEntityListItem[];
    id: number; //作业id
    activityId: number; //活动id
    taskId: number; //任务id
    groupId: number; //小组id
    answerMemberNo: string; //回答小组组长学号
    taskAnswer: string; //任务回答
    score: number; //分数
    remark: string; //评论
}

interface ForCheckStatus {
    id: number; //活动状态id
    activityId: number; //活动id
    taskId: number; //任务id
    groupId: number; //小组id
    memberId: number; //组员id(组长)
    hasCorrected: string; //是否已批阅（0否1是）
    partInTime: string; //参与时间
    partInTimeStr: string; //参与时间str(yyyy-mm-dd hh:mm:ss)
    finished: string; //是否已结束（0否1是）
    status: string; //小组活动状态：0待参与 1待提交 2已参与
}

interface TaskHomeworkForCheck {
    task: ActivityTaskDetail;
    status: ForCheckStatus;
    homework: ForCheckHomework;
    group: ForCheckGroup;
    memberGrade: MemberGradeListItem[];
}

interface ForCheckParams {
    activityId: number; //活动id
    taskId: number; //任务id
    statusId: number; //任务状态id
    homeworkId: number; //作业id
    groupId: number; //小组id
}

/**
 * 批阅小组任务：查询对应的小组作业
 */
export function getGroupActivityTaskHomeworkForCheck(params: ForCheckParams) {
    return axios
        .post<SuccessData<TaskHomeworkForCheck>>(
            "/teacher/courseGroupActivity/getGroupActivityTaskHomeworkForCheck",
            { ...params }
        )
        .then(res => res.data.data);
}

/**
 * 小组成员评分
 * @param params
 */
export function correctGroupMember(
    params: { memberScore: number; id: number; memberId: string }[]
) {
    return axios
        .post<SuccessData<TaskHomeworkForCheck>>(
            "/teacher/courseGroupActivity/correctGroupMember",
            [...params]
        )
        .then(res => res.data.data);
}

interface CorrectGroupActivityTaskParams {
    id?: number; //作业id(不传时为新增操作)
    activityId: number; //小组活动id
    taskId: number; //小组任务id
    groupId: number; //小组id
    score: number; //分数
    remark?: string; //评语
}

/**
 * 批阅小组的任务
 * @param params
 */
export function correctGroupActivityTask(
    params: CorrectGroupActivityTaskParams
) {
    return axios
        .post<SuccessData<TaskHomeworkForCheck>>(
            "/teacher/courseGroupActivity/correctGroupActivityTask",
            { ...params }
        )
        .then(res => res.data.data);
}

/**
 * 查询所有小组的成员得分
 * @param activityId
 * @param keyword
 */
export function getAllGroupMemberGradeList(
    activityId: number,
    keyword: string
) {
    const newKeyWord = keyword.trim();
    return axios
        .post<SuccessData<GroupMemberListItem[]>>(
            "/teacher/courseGroupActivity/findAllGroupMemberGradeList",
            { activityId, keyword: newKeyWord }
        )
        .then(res => res.data.data);
}

export interface GroupAnswerViewItem {
    activityId: number; //活动id
    activityName: string; //活动名称
    taskId: number; //任务id
    taskName: string; //任务名称
    taskQuestion: string; //任务问题
    statusId: number; //任务状态id
    homeworkId: number; //作业id
    groupId: number; //小组id
    groupName: string; //小组名称
    groupScore: string; //小组分数
    groupLeaderName: string; //小组长姓名
    groupCount: number; //小组人数
}

/**
 * 投屏-查询小组回答列表
 * @param activityId
 */
export function getGroupAnswerView(activityId: string) {
    return axios
        .post<SuccessData<GroupAnswerViewItem[]>>(
            "/teacher/courseGroupActivity/findGroupAnswerView",
            { activityId }
        )
        .then(res => res.data.data);
}

/**
 * 修改截止时间
 * @param id 小组活动id
 * @param deadlineStr 截止日期字符串（yyyy-MM-dd HH:mm:ss）
 */
export function updateDeadline(id: number, deadlineStr: Moment) {
    const newTime = moment(deadlineStr).format("YYYY-MM-DD HH:mm:ss");
    return axios.post("/teacher/courseGroupActivity/updateDeadline", {
        id,
        deadlineStr: newTime
    });
}

interface CourseInfoItem {
    id: number; //课程id
    courseno: string; //课程编号
    courseseq: string; //课程序号
    coursename: string; //课程名称
    customerCode: string; //用户代码
    batchNo: string; //学期编号
}

/**
 * 查询当前老师的课程（复制小组活动用）
 */
export function getCourseInfo() {
    return axios
        .post<SuccessData<CourseInfoItem[]>>(
            "/teacher/courseGroupActivity/queryCourseInfo"
        )
        .then(res => res.data.data);
}

export interface CopyActivityListItem {
    id: number; //活动id
    activityName: string; //活动名称
    courseInfo: string; //课程名称信息
    taskCount: string; //任务数
    createTime: string; //创建时间
    publishStatus: PublishStatus;
}

export interface CopyListParams {
    keyword?: string;
    queryParams?: {
        customerCode: string; //用户代码
        courseNo: string; //课程编号
        courseSeq: string; //课程序号
        batchNo: string; //学期编号
    }[];
}

/**
 * 复制小组活动-查询小组活动列表（复制小组活动用）
 * @param params
 */
export function getGroupActivityListForCopy(params: CopyListParams) {
    const text = params.keyword?.trim();
    return axios
        .post<SuccessData<CopyActivityListItem[]>>(
            "/teacher/courseGroupActivity/findGroupActivityListForCopy",
            { ...params, keyword: text }
        )
        .then(res => res.data.data);
}

interface CopyParams extends activityListParams {
    ids: string;
}

/**
 * 复制小组活动
 * @param params
 */
export function copyGroupActivity(params: CopyParams) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const customerCode = Config.CustomCode;
    return axios.post("/teacher/courseGroupActivity/copyGroupActivity", {
        ...params,
        batchNo,
        customerCode
    });
}

//执行随机分组
export interface DoRandomGroup {
    courseNo: string; //课程编号
    courseSeq: string; //课程序号
    randomGroupKey?: string;
    signInId?: number;
    weektimeId: number;
    groupCount?: number;
    groupName?: string;
}
export interface DoRandomData {
    randomGroupKey: string;
    signInId: number;
    groupList: GroupListItem[];
}
export function previewOrExecuteRandomGroup(params: DoRandomGroup) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const customerCode = Config.CustomCode;
    return axios
        .post<SuccessData<DoRandomData>>(
            "/teacher/courseGroup/autoRandomGroup",
            {
                ...params,
                batchNo,
                customerCode
            }
        )
        .then(res => res.data.data);
}

//查看随机分组签到
export interface GroupSignList {
    id: string;
    title: string;
    signInStuCount: number;
}
export function queryCurrentWeektimeSign(weektimeId: number) {
    return axios
        .get<SuccessData<GroupSignList[]>>(
            `/teacher/courseGroup/queryCurrentWeektimeSign/${weektimeId}`
        )
        .then(res => res.data.data);
}

//获取分组人数

export function getGroupPersonNumber(
    groupNumber: number | undefined,
    studentNumber: number | undefined
) {
    const val = handleScore(studentNumber || 0, groupNumber || 0);
    const allCounts = studentNumber || 0;
    if (val === 0) {
        return "0";
    }
    const num = allCounts / val;
    //判断是否是整数
    const isInt = _.isInteger(num);
    const minNum = parseInt(num + "");
    return isInt ? num + "" : `${minNum}~${Number(minNum) + 1}`;
}

//获取随机分组
export function queryRandomGroup(params: DoRandomGroup) {
    const batchNo = Storage.get("batch") || Config.DefaultBatch;
    const customerCode = Config.CustomCode;
    return axios
        .post<SuccessData<DoRandomData>>(
            "/teacher/courseGroup/previewRandomGroup",
            {
                ...params,
                batchNo,
                customerCode
            }
        )
        .then(res => res.data.data.groupList);
}
